import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import LandingNavbar from '../../components/LandingNavbar';
import { brand } from '../../config/brand';

export default function PrivacyPage(): JSX.Element {
  const token = localStorage.getItem('token');

  return (
    <div className="min-h-screen bg-background text-foreground selection:bg-primary/30">
      <LandingNavbar />

      <div className="container mx-auto px-4 py-24 max-w-4xl">
        <h1 className="text-4xl md:text-6xl font-black text-foreground tracking-tighter mb-8">Privacy Policy</h1>
        <div className="space-y-8 text-muted-foreground leading-relaxed font-medium">
          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4 uppercase tracking-tight">1. Information We Collect</h2>
            <p>
              We collect information you provide directly to us when you create an account, such as your name, email address, and any information about your game projects. We also collect data from the social media accounts you connect to the Service as necessary to provide our features.
            </p>
            <p className="mt-4">
              <strong className="text-foreground">Account Data:</strong> Email, name, password (hashed), avatar, and billing information processed through our payment provider (Polar).
            </p>
            <p className="mt-4">
              <strong className="text-foreground">Social Account Data:</strong> When you connect social media accounts, we store authentication tokens, account handles, display names, and profile pictures necessary to publish content on your behalf.
            </p>
            <p className="mt-4">
              <strong className="text-foreground">Content Data:</strong> Posts you create, media files you upload, scheduling preferences, and publishing history.
            </p>
            <p className="mt-4">
              <strong className="text-foreground">Usage Data:</strong> We collect logs of actions taken within the platform (e.g., posts published, features used) to improve the Service and detect abuse.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4 uppercase tracking-tight">2. Platform Integrations</h2>
            <p>
              {brand.name} integrates with the following third-party platforms to provide multi-platform publishing and analytics. Each integration accesses only the data necessary to provide the Service.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-primary mb-3">2.1 TikTok API Services</h3>
            <p>
              {brand.name} uses TikTok API Services to allow users to upload videos and view performance statistics. By using these features, you agree to be bound by the <a href="https://www.tiktok.com/legal/terms-of-service" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">TikTok Terms of Service</a> and the <a href="https://www.tiktok.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">TikTok Privacy Policy</a>.
            </p>
            <div className="mt-4 space-y-4">
              <p>
                <strong className="text-foreground">What data we access:</strong> When you connect your TikTok account, we access your open_id, display name, and avatar. We also request permission to upload videos on your behalf and read your post status.
              </p>
              <p>
                <strong className="text-foreground">How we use this data:</strong> We use your account information to identify your account in our dashboard. We use upload permissions only when you explicitly schedule or post a video through our interface.
              </p>
              <p>
                <strong className="text-foreground">Data Storage:</strong> We store your authentication tokens securely to maintain your connection. We do not share your TikTok user data with any third parties or external AI models.
              </p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-primary mb-3">2.2 YouTube API Services & Google Data</h3>
            <p>
              {brand.name} uses YouTube API Services to allow users to upload videos and view performance statistics. By using these features, you agree to be bound by the <a href="https://www.youtube.com/t/terms" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">YouTube Terms of Service</a> and the <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Google Privacy Policy</a>.
            </p>
            <div className="mt-4 space-y-4">
              <p>
                <strong className="text-foreground">What data we access:</strong> When you connect your YouTube account, we access your channel name, profile picture, and video metadata. We also request permission to upload videos on your behalf and read your YouTube Analytics data.
              </p>
              <p>
                <strong className="text-foreground">How we use this data:</strong> We use your channel information to identify your account in our dashboard. We use upload permissions only when you explicitly schedule or post a video through our interface. We use analytics data to show you the performance of your videos within our dashboard.
              </p>
              <p>
                <strong className="text-foreground">Data Storage and Sharing:</strong> We store your authentication tokens securely in our encrypted database to maintain your connection. We do not share your Google user data with any third parties or external AI models. You can revoke {brand.name}'s access to your data at any time via the <a href="https://security.google.com/settings/security/permissions" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Google Security Settings page</a>.
              </p>
              <p className="bg-primary/10 border border-primary/20 p-4 rounded-xl text-sm italic">
                {brand.name}'s use and transfer to any other app of information received from Google APIs will adhere to <a href="https://developers.google.com/terms/api-services-user-data-policy#limited-use" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-bold">Google API Services User Data Policy</a>, including the Limited Use requirements.
              </p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-primary mb-3">2.3 Instagram & Threads (Meta)</h3>
            <p>
              {brand.name} uses browser automation (Playwright) to publish content to Instagram and Threads on your behalf. By connecting these accounts, you acknowledge:
            </p>
            <div className="mt-4 space-y-4">
              <p>
                <strong className="text-foreground">What data we store:</strong> Your session credentials (encrypted) to maintain your connection to Instagram/Threads. We do not access your DMs, follower lists, or personal data beyond what is necessary to publish content.
              </p>
              <p>
                <strong className="text-foreground">How we use this data:</strong> Solely to publish text, images, and videos that you explicitly schedule or post through our interface.
              </p>
              <p>
                <strong className="text-foreground">Meta Terms:</strong> You remain subject to <a href="https://help.instagram.com/581066165581870" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Instagram Terms of Use</a> and <a href="https://www.facebook.com/legal/terms" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Meta Terms of Service</a>.
              </p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-primary mb-3">2.4 Twitter / X</h3>
            <p>
              {brand.name} connects to Twitter/X via its API to publish tweets, threads, and media on your behalf.
            </p>
            <div className="mt-4 space-y-4">
              <p>
                <strong className="text-foreground">What data we access:</strong> Your account handle, display name, and profile picture. We request permission to post tweets and upload media.
              </p>
              <p>
                <strong className="text-foreground">How we use this data:</strong> Solely to publish content that you explicitly create, schedule, or post through our interface.
              </p>
              <p>
                <strong className="text-foreground">Terms:</strong> You remain subject to the <a href="https://twitter.com/en/tos" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">X Terms of Service</a>.
              </p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-primary mb-3">2.5 Bluesky</h3>
            <p>
              {brand.name} connects to Bluesky via the AT Protocol to publish posts and media on your behalf.
            </p>
            <div className="mt-4 space-y-4">
              <p>
                <strong className="text-foreground">What data we store:</strong> Your Bluesky handle and authentication credentials (app password, encrypted). We do not access your DMs or social graph.
              </p>
              <p>
                <strong className="text-foreground">Terms:</strong> You remain subject to the <a href="https://bsky.social/about/support/tos" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Bluesky Terms of Service</a>.
              </p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-primary mb-3">2.6 Discord</h3>
            <p>
              {brand.name} uses Discord webhooks or bot integrations to publish messages and media to your specified Discord channels.
            </p>
            <div className="mt-4 space-y-4">
              <p>
                <strong className="text-foreground">What data we store:</strong> Webhook URLs or bot tokens (encrypted) for the channels you configure. We do not access server member lists, DMs, or any other server data.
              </p>
              <p>
                <strong className="text-foreground">Terms:</strong> You remain subject to the <a href="https://discord.com/terms" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Discord Terms of Service</a>.
              </p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-primary mb-3">2.7 Telegram</h3>
            <p>
              {brand.name} uses the Telegram Bot API to publish messages and media to your specified Telegram channels or groups.
            </p>
            <div className="mt-4 space-y-4">
              <p>
                <strong className="text-foreground">What data we store:</strong> Your bot token and channel/group identifiers (encrypted). We do not access private messages or member lists.
              </p>
              <p>
                <strong className="text-foreground">Terms:</strong> You remain subject to the <a href="https://telegram.org/tos" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Telegram Terms of Service</a>.
              </p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-primary mb-3">2.8 Tumblr</h3>
            <p>
              {brand.name} connects to Tumblr via its API to publish posts and media on your behalf.
            </p>
            <div className="mt-4 space-y-4">
              <p>
                <strong className="text-foreground">What data we access:</strong> Your blog name and authentication tokens. We do not access your dashboard feed, followers, or DMs.
              </p>
              <p>
                <strong className="text-foreground">Terms:</strong> You remain subject to the <a href="https://www.tumblr.com/policy/en/terms-of-service" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Tumblr Terms of Service</a>.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4 uppercase tracking-tight">3. AI Features</h2>
            <p>
              {brand.name} offers AI-powered writing features (copilot, rephrasing, grammar correction, translation) for Indie and higher tier plans. These features send your post text to third-party AI providers (OpenAI) for processing.
            </p>
            <div className="mt-4 space-y-4">
              <p>
                <strong className="text-foreground">What data is sent:</strong> Only the specific text content you submit for AI processing. We do not send your account information, social credentials, or analytics data to AI providers.
              </p>
              <p>
                <strong className="text-foreground">Data retention:</strong> AI providers process your text in real-time and we do not store AI processing logs. Refer to <a href="https://openai.com/policies/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">OpenAI's Privacy Policy</a> for their data handling practices.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4 uppercase tracking-tight">4. Payment Processing</h2>
            <p>
              Subscription payments are processed through <a href="https://polar.sh" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Polar</a>. We do not store your credit card numbers or banking details on our servers. Polar handles all payment data in accordance with PCI-DSS standards. We only store your subscription status, plan tier, and Polar customer/subscription identifiers.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4 uppercase tracking-tight">5. How We Use Your Information</h2>
            <p>
              We use the information we collect to:
            </p>
            <ul className="list-disc pl-6 mt-4 space-y-2">
              <li>Provide, maintain, and improve the Service.</li>
              <li>Schedule and publish posts to your connected social media accounts.</li>
              <li>Analyze content trends and provide top post performance insights.</li>
              <li>Process AI-powered writing assistance when you use those features.</li>
              <li>Process payments and manage your subscription.</li>
              <li>Communicate with you about updates, features, and support.</li>
              <li>Detect and prevent abuse, fraud, and violations of our Terms of Service.</li>
              <li>Analyze usage patterns to optimize user experience.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4 uppercase tracking-tight">6. Data Sharing and Disclosure</h2>
            <p>
              We do not sell your personal data to third parties. We may share information:
            </p>
            <ul className="list-disc pl-6 mt-4 space-y-2">
              <li>With social media platforms to publish content as requested by you.</li>
              <li>With AI service providers (OpenAI) to process text when you use AI features.</li>
              <li>With our payment processor (Polar) to manage your subscription.</li>
              <li>With service providers who perform functions on our behalf (e.g., hosting, analytics).</li>
              <li>If required by law or to protect the rights and safety of {brand.name} or others.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4 uppercase tracking-tight">7. Data Security</h2>
            <p>
              We implement industry-standard security measures to protect your information from unauthorized access, disclosure, or destruction. All social media authentication tokens and credentials are stored encrypted. API communications use HTTPS/TLS encryption. However, no method of transmission over the internet or electronic storage is 100% secure.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4 uppercase tracking-tight">8. Data Retention</h2>
            <p>
              We retain your account data for as long as your account is active. If you delete your account, we will delete your personal data, social media tokens, and content within 30 days, except where retention is required by law or necessary to resolve disputes. Anonymized usage analytics may be retained indefinitely.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4 uppercase tracking-tight">9. Your Choices and Rights</h2>
            <p>
              You can access, update, or delete your account information through your settings page at any time. You can also disconnect social media accounts, which will immediately revoke our access and delete stored tokens for those platforms. Under GDPR and similar regulations, you have the right to:
            </p>
            <ul className="list-disc pl-6 mt-4 space-y-2">
              <li>Request a copy of the personal data we hold about you.</li>
              <li>Request correction of inaccurate data.</li>
              <li>Request deletion of your data ("right to be forgotten").</li>
              <li>Object to or restrict processing of your data.</li>
              <li>Data portability (receive your data in a structured format).</li>
            </ul>
            <p className="mt-4">
              To exercise these rights, contact us at <a href={`mailto:${brand.email}`} className="text-primary hover:underline">{brand.email}</a>.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4 uppercase tracking-tight">10. Third-Party Links and Services</h2>
            <p>
              The Service contains links to third-party websites and services. We are not responsible for the privacy practices of these third parties, and we encourage you to read their privacy policies.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4 uppercase tracking-tight">11. Cookies and Tracking</h2>
            <p>
              We use cookies and similar technologies to track activity on our Service and hold certain information to improve your user experience and for authentication purposes. We use:
            </p>
            <ul className="list-disc pl-6 mt-4 space-y-2">
              <li><strong className="text-foreground">Authentication cookies:</strong> JWT tokens stored in localStorage to keep you logged in.</li>
              <li><strong className="text-foreground">Preference cookies:</strong> To remember your settings (e.g., billing cycle preference, GDPR consent).</li>
              <li><strong className="text-foreground">Analytics:</strong> Basic usage tracking to improve the Service. We do not use third-party advertising trackers.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4 uppercase tracking-tight">12. Changes to This Policy</h2>
            <p>
              We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date below.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4 uppercase tracking-tight">13. Contact Us</h2>
            <p>
              If you have questions about this Privacy Policy or our data practices, contact us at <a href={`mailto:${brand.email}`} className="text-primary hover:underline">{brand.email}</a>.
            </p>
          </section>

          <div className="pt-12 border-t border-border">
            <p className="text-sm">Last updated: February 18, 2026</p>
          </div>
        </div>
      </div>
    </div>
  );
}
